import React, { useState, useContext } from 'react';
import { Box, Image, Text, VStack, Flex, HStack, Icon } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiAlertTriangle } from 'react-icons/fi';
import { goLiveLabels } from 'features/collabs/utils/postByLabels';
import { Campaign, Client, Collab, FoodfluenceLocation, daysUntilPostBy, windowOf } from '@foodfluence/shared';
import SignInSignUpModal from './SignInSignUpModal';
import RequestCollabModal from './RequestCollabModal';
import { auth } from '../../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { formatFirebaseDate } from '../../../utils/dateUtils';
import { getBrowserTimezone } from 'utils/timezoneUtils';
import { WhiteLabelContext } from '../../../contexts/WhiteLabelContext';
import { getEffectiveEndDate } from 'utils/collabUtils';
import { MoneyFigure } from '../../../components/ui';
import { ff } from '../../../theme';
import { CAMPAIGN_TYPE_CHIP, getBrandImage, showsCredit } from '../utils';

interface CampaignComponentProps {
  campaign: Campaign & { locations: FoodfluenceLocation[] };
  client: Client;
  requestedCollab?: Collab;
  isExplorePage?: boolean;
  /** Kept for callers; the board card has no location line. */
  locationFilter?: { lat: number; lng: number };
}

export const ImageChip: React.FC<{ children: React.ReactNode; tone: 'ink' | 'white' | 'warn' }> = ({ children, tone }) => {
  const tones = {
    ink: { bg: ff.ink, color: ff.lime, fontWeight: 600, letterSpacing: '0.1em' },
    white: { bg: 'white', color: ff.ink },
    // Board's urgent pill (Bounties): #FBE1DC / #6E1A0A
    warn: { bg: '#FBE1DC', color: '#6E1A0A' },
  };
  return (
    <Flex
      as="span"
      h="28px"
      px="10px"
      borderRadius="full"
      align="center"
      gap="4px"
      fontSize="11px"
      fontWeight={600}
      letterSpacing="0.08em"
      textTransform="uppercase"
      whiteSpace="nowrap"
      {...tones[tone]}
    >
      {children}
    </Flex>
  );
};

/** Brand-page card money: value (30px) stacked over a grey label (board Brand.dc). */
const StackedFigure: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <VStack spacing="4px" align="flex-end">
    <Text textStyle="display" fontSize="30px" textTransform="none" letterSpacing="0" color={value ? ff.ink : ff.faint}>
      ${value}
    </Text>
    <Text textStyle="eyebrow" fontSize="11px" letterSpacing="0.08em" lineHeight="normal" color={ff.text2}>
      {label}
    </Text>
  </VStack>
);

const CampaignComponent: React.FC<CampaignComponentProps> = ({ campaign, client, requestedCollab, isExplorePage }) => {
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [user] = useAuthState(auth);
  const { isWhiteLabeled } = useContext(WhiteLabelContext);
  const navigate = useNavigate();

  const credit = requestedCollab?.proposedInfluencerCredit ?? campaign?.credit ?? 0;
  const cash = requestedCollab?.proposedInfluencerCash ?? campaign?.cash ?? 0;
  // Only the amounts the creator gets: hide $0 cash when there's credit, and $0 credit always.
  const showCredit = showsCredit(campaign?.campaignType, credit) && credit > 0;
  const productValue = campaign?.productValue ?? 0;
  const showCash = cash > 0 || (!showCredit && !productValue);
  const productOnly = !showCash && !showCredit && productValue > 0;

  // Go-live campaigns: "Post on Oct 28" / "Post between Oct 26 and Nov 1"; within 3 days of the
  // window's end it is highlighted with an icon and the days left. Turnaround-only: no badge.
  const goLive = windowOf(campaign);
  const postByDaysLeft = goLive ? daysUntilPostBy(goLive.end, new Date(), getBrowserTimezone()) : null;
  const postBySoon = postByDaysLeft !== null && postByDaysLeft <= 3;
  const postByText = goLive
    ? `${goLiveLabels(goLive).post}${
        postBySoon ? ` · ${postByDaysLeft <= 0 ? 'today' : postByDaysLeft === 1 ? '1 day left' : `${postByDaysLeft} days left`}` : ''
      }`
    : null;

  const endDate = getEffectiveEndDate(campaign, requestedCollab);
  const statusText = requestedCollab
    ? `Sent ${formatFirebaseDate(requestedCollab.collabRequestedTime)}`
    : endDate
      ? `Ends ${formatFirebaseDate(endDate)}`
      : null;

  const title = campaign?.name ?? 'Custom Collab';

  const handleCampaignClick = () => {
    if (!user) {
      setIsSignInModalOpen(true);
      return;
    }
    // Pending requests edit in place; white-label has no campaign page, so it goes straight to the form
    if (requestedCollab || isWhiteLabeled) {
      setIsRequestModalOpen(true);
      return;
    }
    navigate(`/explore/client/${client.id}/campaign/${campaign.id}`, { state: { campaign, client } });
  };

  return (
    <>
      <Box
        as="button"
        type="button"
        onClick={handleCampaignClick}
        aria-label={`${client.accountName}: ${title}`}
        display="flex"
        flexDirection="column"
        gap={isExplorePage ? '12px' : '10px'}
        w="100%"
        textAlign="left"
        color={ff.ink}
      >
        <Box position="relative" h={isExplorePage ? '230px' : '190px'} w="100%" borderRadius="20px" overflow="hidden" bg={ff.grey}>
          <Image
            src={getBrandImage(campaign, client)}
            alt=""
            h="100%"
            w="100%"
            objectFit="cover"
          />
          <HStack position="absolute" top="12px" left="12px" spacing="6px">
            {campaign?.campaignType && <ImageChip tone="white">{CAMPAIGN_TYPE_CHIP[campaign.campaignType]}</ImageChip>}
            {campaign?.creatorType === 'ugc' && <ImageChip tone="ink">UGC</ImageChip>}
          </HStack>
          {postByText && (
            <Box position="absolute" bottom="12px" left="12px">
              <ImageChip tone={postBySoon ? 'warn' : 'white'}>
                <Icon as={postBySoon ? FiAlertTriangle : FiCalendar} boxSize="12px" aria-hidden />
                {postByText}
              </ImageChip>
            </Box>
          )}
        </Box>

        <Flex align="flex-start" gap="16px" w="100%">
          <VStack flex={1} minW={0} align="stretch" spacing="4px">
            {isExplorePage && (
              <Text textStyle="display" fontSize="21px" lineHeight="1.1" letterSpacing="0" fontWeight={600} noOfLines={2}>
                {client.accountName}
              </Text>
            )}
            <Text fontSize="15px" lineHeight="1.35" fontWeight={isExplorePage ? 400 : 600} noOfLines={2}>
              {title}
            </Text>
            {statusText && (
              <Text fontSize="12px" color={ff.muted}>
                {statusText}
              </Text>
            )}
          </VStack>
          <VStack
            flex="none"
            minW={isExplorePage ? '84px' : undefined}
            align="flex-end"
            spacing={isExplorePage ? '6px' : '4px'}
            pl="16px"
            borderLeft="1px solid"
            borderColor={ff.line}
          >
            {isExplorePage ? (
              <>
                {showCash && <MoneyFigure size="21px" value={`$${cash}`} label="Cash" />}
                {showCredit && <MoneyFigure size="21px" value={`$${credit}`} label="Credit" />}
                {productOnly && <MoneyFigure size="21px" value={`$${Math.round(productValue)}`} label="Product" />}
              </>
            ) : (
              <>
                {showCash && <StackedFigure value={cash} label="Cash" />}
                {showCredit && <StackedFigure value={credit} label="Credit" />}
                {productOnly && <StackedFigure value={Math.round(productValue)} label="Product" />}
              </>
            )}
            {productValue > 0 && !productOnly && (
              <Text as="span" mt="2px" px="7px" py="3px" borderRadius="6px" bg={ff.lime} color={ff.ink} fontSize="12px" fontWeight={600} lineHeight="normal" whiteSpace="nowrap">
                +${Math.round(productValue)} product
              </Text>
            )}
          </VStack>
        </Flex>
      </Box>

      <SignInSignUpModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
        returnTo={isWhiteLabeled ? "/request-collab" : `/explore/client/${client.id}/request-collab`}
        returnState={{
          campaign: campaign,
          client: client,
          requestedCollab: requestedCollab
        }}
      />

      {/* Sibling of the clickable card so sheet clicks don't re-trigger handleCampaignClick */}
      <RequestCollabModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        campaign={campaign}
        client={client}
        requestedCollab={requestedCollab}
      />
    </>
  );
};

export default React.memo(CampaignComponent);
